import axios from 'axios';

import { customFetch } from '../lib/network/Axios';
import { handleError } from '../lib/ErrorHandler';
import type {
  BillingActionResponse,
  CancelInvoiceRequest,
  IssueMonthlyInvoicesRequest,
  IssueSetupInvoiceRequest,
  KioskBillingInvoice,
  KioskBillingInvoiceStatus,
  KioskBillingInvoiceType,
  KioskOwnerPayoutRequest,
  MarkInvoicePaidRequest,
  PayoutRequestStatus,
} from '../types/kioskBilling.types';

function extractMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data;
    if (typeof responseData?.message === 'string' && responseData.message.trim()) {
      return responseData.message;
    }
    if (Array.isArray(responseData?.message) && responseData.message.length > 0) {
      const first = responseData.message[0];
      if (typeof first === 'string' && first.trim()) {
        return first;
      }
    }
    if (typeof responseData === 'string' && responseData.trim()) {
      return responseData;
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
}

export interface InvoiceQueryParams {
  kioskId?: string;
  status?: KioskBillingInvoiceStatus | '';
  invoiceType?: KioskBillingInvoiceType | '';
  overdueOnly?: boolean;
}

export const getKioskBillingInvoices = async (params?: InvoiceQueryParams): Promise<KioskBillingInvoice[]> => {
  try {
    const response = await customFetch.get('/kiosk-billing/invoices', {
      params: {
        kioskId: params?.kioskId || undefined,
        status: params?.status || undefined,
        invoiceType: params?.invoiceType || undefined,
        overdueOnly: params?.overdueOnly ? 'true' : undefined,
      },
    });

    return Array.isArray(response.data) ? (response.data as KioskBillingInvoice[]) : [];
  } catch (error) {
    handleError(error);
    throw new Error(extractMessage(error, 'Failed to fetch kiosk billing invoices'));
  }
};

export const issueSetupInvoice = async (payload: IssueSetupInvoiceRequest): Promise<BillingActionResponse> => {
  try {
    const response = await customFetch.post('/kiosk-billing/invoices/setup/issue', payload);
    return response.data as BillingActionResponse;
  } catch (error) {
    handleError(error);
    throw new Error(extractMessage(error, 'Failed to issue setup invoice'));
  }
};

export const issueMonthlyInvoices = async (
  payload: IssueMonthlyInvoicesRequest,
): Promise<BillingActionResponse> => {
  try {
    const response = await customFetch.post('/kiosk-billing/invoices/monthly/issue', payload);
    return response.data as BillingActionResponse;
  } catch (error) {
    handleError(error);
    throw new Error(extractMessage(error, 'Failed to issue monthly invoices'));
  }
};

export const markInvoicePaid = async (
  invoiceId: number,
  payload?: MarkInvoicePaidRequest,
): Promise<BillingActionResponse> => {
  try {
    const response = await customFetch.post(`/kiosk-billing/invoices/${invoiceId}/mark-paid`, payload || {});
    return response.data as BillingActionResponse;
  } catch (error) {
    handleError(error);
    throw new Error(extractMessage(error, 'Failed to mark invoice paid'));
  }
};

export const cancelInvoice = async (
  invoiceId: number,
  payload?: CancelInvoiceRequest,
): Promise<BillingActionResponse> => {
  try {
    const response = await customFetch.post(`/kiosk-billing/invoices/${invoiceId}/cancel`, payload || {});
    return response.data as BillingActionResponse;
  } catch (error) {
    handleError(error);
    throw new Error(extractMessage(error, 'Failed to cancel invoice'));
  }
};

export const runKioskBillingOverdueJob = async (): Promise<BillingActionResponse> => {
  try {
    const response = await customFetch.post('/kiosk-billing/jobs/run-overdue-blocking');
    return response.data as BillingActionResponse;
  } catch (error) {
    handleError(error);
    throw new Error(extractMessage(error, 'Failed to run kiosk billing job'));
  }
};

export const getKioskPayoutRequests = async (params?: {
  ownerEmail?: string;
  kioskId?: string;
  status?: PayoutRequestStatus | '';
}): Promise<KioskOwnerPayoutRequest[]> => {
  try {
    const response = await customFetch.get('/kiosk-billing/payout-requests', {
      params: {
        ownerEmail: params?.ownerEmail || undefined,
        kioskId: params?.kioskId || undefined,
        status: params?.status || undefined,
      },
    });

    return Array.isArray(response.data) ? (response.data as KioskOwnerPayoutRequest[]) : [];
  } catch (error) {
    handleError(error);
    throw new Error(extractMessage(error, 'Failed to fetch payout requests'));
  }
};

export const updateKioskPayoutRequestStatus = async (
  requestId: number,
  payload: { status: PayoutRequestStatus; adminNote?: string },
): Promise<{ message: string; request: KioskOwnerPayoutRequest | null }> => {
  try {
    const response = await customFetch.post(`/kiosk-billing/payout-requests/${requestId}/status`, payload);
    return response.data as { message: string; request: KioskOwnerPayoutRequest | null };
  } catch (error) {
    handleError(error);
    throw new Error(extractMessage(error, 'Failed to update payout request status'));
  }
};